<template>
  <v-container grid-list-xl fluid class="accounting-container">
    <!-- Header Summary Block -->
    <div class="d-flex align-start justify-space-between flex-wrap mb-4" style="gap: 16px;">
      <div>
        <h1 class="accounting-title font-weight-bold">Kế toán — duyệt đơn trước khi giao</h1>
        <div class="accounting-subtitle">
          Gốc <b>Khách hàng</b>. Thanh toán tính <b>gộp theo khách</b>. Tick chọn đơn → xác nhận → kiện chuyển sang <b>Chờ giao</b>.
        </div>
      </div>
      
      <!-- KPI Cards Row -->
      <div class="d-flex flex-wrap align-center" style="gap: 12px;">
        <div class="kpi-card">
          <div class="kpi-title">Khách hàng</div>
          <div class="kpi-value info--text">{{ totalCustomers }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-title">Tổng đơn (về VN)</div>
          <div class="kpi-value text-dark">{{ totalOrders }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-title">Đã xác nhận</div>
          <div class="kpi-value success--text">{{ confirmedCount }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-title">Chưa xác nhận</div>
          <div class="kpi-value error--text">{{ unconfirmedCount }}</div>
        </div>
        <div class="kpi-card card-shortfall">
          <div class="kpi-title">Tổng thiếu (sau ví)</div>
          <div class="kpi-value val-shortfall">{{ totalShortfall | currency }}</div>
        </div>
      </div>
    </div>

    <!-- Filters Row -->
    <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px;">
      <v-text-field 
        placeholder="Tìm mọi giá trị: đơn, khách, mã..." 
        v-model="quickSearchTerm"
        prepend-inner-icon="fa-search" 
        outlined 
        dense 
        hide-details
        clearable
        class="filter-search"
        style="max-width: 320px; background: #fff;"
        @input="onSearchInput"
        @click:clear="onClearSearch"
      ></v-text-field>
      <!-- Lọc theo khách hàng -->
      <v-autocomplete
        :items="customerList"
        item-text="CustomerName"
        item-value="ID"
        v-model="selectedCustomerFilter"
        outlined
        dense
        hide-details
        class="filter-select"
        style="max-width: 220px; background: #fff;"
        @change="onCustomerFilterChange"
      ></v-autocomplete>
      <!-- Lọc theo trạng thái thanh toán -->
      <v-select
        :items="paymentFilterList"
        item-text="text"
        item-value="value"
        v-model="selectedPaymentFilter"
        outlined
        dense
        hide-details
        class="filter-select"
        style="max-width: 180px; background: #fff;"
        @change="onPaymentFilterChange"
      ></v-select>
      <!-- Lọc theo tình trạng đã duyệt xuất kho -->
      <v-select
        :items="deliverableFilterList"
        item-text="text"
        item-value="value"
        v-model="selectedDeliverableFilter"
        outlined
        dense
        hide-details
        class="filter-select"
        style="max-width: 240px; background: #fff;"
        @change="onDeliverableFilterChange"
      ></v-select>

      <v-spacer></v-spacer>
      
      <!-- Pagination controls -->
      <div class="d-flex align-center" style="gap: 8px;">
        <span style="font-size: 13px; color: #666;">Số dòng mỗi trang:</span>
        <v-select
          :items="[10, 20, 50, 100]"
          v-model="pagination.rowsPerPage"
          dense
          outlined
          hide-details
          style="max-width: 80px; background: #fff;"
          @change="onRowsPerPageChange"
        ></v-select>
        <v-btn icon small :disabled="pagination.page <= 1" @click="prevPage"><v-icon size="16">fa-chevron-left</v-icon></v-btn>
        <span style="font-size: 13px; font-weight: 500;">Trang {{ pagination.page }} / {{ totalPages }}</span>
        <v-btn icon small :disabled="pagination.page >= totalPages" @click="nextPage"><v-icon size="16">fa-chevron-right</v-icon></v-btn>
      </div>
    </div>

    <!-- Bulk Selection Bar -->
    <div v-if="selectedOrders.length > 0" class="bulk-action-bar d-flex align-center mb-3">
      <v-icon color="primary" size="16" class="mr-2">fa-check-square</v-icon>
      <span class="bulk-text">
        Đang chọn <b class="primary--text">{{ selectedOrders.length }} đơn</b> · {{ selectedStats.packages }} kiện · {{ selectedStats.rows }} VĐ
      </span>
      <span v-if="selectedStats.shortfall > 0" class="bulk-shortfall ml-2 font-weight-bold red--text">
        · Thiếu ví gộp {{ selectedStats.shortfall | currency }}
      </span>
      <v-btn 
        :color="selectedStats.shortfall > 0 ? 'error' : 'primary'" 
        small 
        class="ml-3 text-none font-weight-bold px-3" 
        @click="openBulkConfirm"
      >
        <v-icon size="13" class="mr-1">fa-clipboard-check</v-icon> Xác nhận {{ selectedOrders.length }} đơn
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn icon small @click="clearSelection"><v-icon size="14" color="grey">fa-times</v-icon></v-btn>
    </div>

    <!-- Main Accounting Table -->
    <v-card outlined class="border-radius-8 overflow-hidden mb-3">
      <div class="accounting-table-wrapper">
        <div class="accounting-table-header">
          <div class="col-check">
            <input type="checkbox" :checked="isAllSelected" @change="toggleSelectAll">
          </div>
          <div class="col-code">Đơn CN · Trạng thái</div>
          <div class="col-goods">Hàng hóa (kê khai)</div>
          <div class="col-value">Giá trị & Thuế phí</div>
          <div class="col-packages">Mã VĐ · Kiện · Kg · CBM · Xe</div>
          <div class="col-note">Ghi chú</div>
          <div class="col-action">Xác nhận</div>
        </div>

        <div v-if="loading" class="pa-10 text-center">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <div class="mt-2 value-item">Đang tải dữ liệu...</div>
        </div>

        <div v-else-if="filteredPayOrders.length === 0" class="pa-10 text-center value-item">
          Không tìm thấy dữ liệu phù hợp.
        </div>

        <div v-else v-for="cust in filteredPayOrders" :key="cust.ID" class="customer-group">
          <!-- Customer Header Row -->
          <div class="customer-row">
            <div class="col-check">
              <input type="checkbox" :checked="isCustomerAllSelected(cust.ID)" @change="toggleSelectCustomer(cust)">
            </div>
            <div class="customer-info-wrapper">
              <div class="customer-info-main">
                <v-icon color="primary" size="16">fa-user-circle</v-icon>
                <span class="customer-name">{{ cust.CustomerName }}</span>
                <span v-if="getCustomerPhone(cust)" class="ml-1 grey--text text--darken-1 font-weight-regular" style="font-size: 13px;">({{ getCustomerPhone(cust) }})</span>
                <span class="customer-meta">
                  KH {{ cust.UserCode }} · Sale: {{ getCustomerStaffName(cust, 'SalerName') }} · CSKH: {{ getCustomerStaffName(cust, 'CSName') }} · XNK: {{ getCustomerStaffName(cust, 'OfficerName') }} · {{ getVisibleOrderCount(cust.ID) }} đơn · {{ getCustomerTotalPackages(cust.ID) }} kiện
                </span>
              </div>
              <div v-if="canViewCustomerAddress && getCustomerAddress(cust)" class="customer-address-line">
                <span class="customer-address-text">Địa chỉ: {{ getCustomerAddress(cust) }}</span>
              </div>
            </div>
            <div class="customer-action-wrapper">
              <v-btn 
                v-slot:default
                v-if="hasUndeliveredOrders(cust.ID)"
                :color="cust.Wallet >= cust.PayLeft ? 'primary' : 'error'" 
                class="text-none font-weight-bold" 
                small 
                outlined
                @click="confirmCustomer(cust)"
              >
                <v-icon size="12" class="mr-1">fa-clipboard-check</v-icon>
                Xác nhận cả khách ({{ getUndeliveredCount(cust.ID) }})
              </v-btn>
              <v-btn
                v-if="hasConfirmedUnpaidOrders(cust.ID)"
                color="success"
                class="text-none font-weight-bold ml-2"
                small
                @click="openCustomerPaymentConfirm(cust)"
              >
                <v-icon size="12" class="mr-1">fa-wallet</v-icon>
                Thanh toán cả khách ({{ getConfirmedUnpaidCount(cust.ID) }})
              </v-btn>
            </div>
          </div>

          <!-- Customer Finance Sub-bar -->
          <div class="customer-finance-bar">
            <span class="finance-label">💰 Thanh toán (gộp theo khách):</span>
            <span class="finance-item">Tổng giá trị đơn: <b>{{ cust.TotalAmountVND | currency }}</b></span>
            <span class="finance-item">Tổng đã cọc: <b class="success--text">{{ cust.Deposit | currency }}</b></span>
            <span class="finance-item">Thiếu tổng: <b class="error--text">{{ cust.PayLeft | currency }}</b></span>
            <span class="finance-item">Số dư ví: <b>{{ cust.Wallet | currency }}</b></span>
            <span class="finance-item">Dư sau xuất: <b :class="cust.Wallet - cust.PayLeft < 0 ? 'red--text' : 'success--text'">{{ (cust.Wallet - cust.PayLeft) | currency }}</b></span>
            <span class="finance-status-badge ml-2" :class="cust.Wallet >= cust.PayLeft ? 'badge-sufficient' : 'badge-shortfall'">
              {{ cust.Wallet >= cust.PayLeft ? 'Đã đủ' : 'Thiếu tiền' }}
            </span>
          </div>

          <!-- Order Rows under Customer -->
          <div v-for="order in getCustomerOrders(cust.ID)" :key="order.ID" class="order-item-wrapper" :class="{ 'order-selected': isOrderSelected(order.ID) }">
            <div class="order-row">
              <div class="col-check">
                <v-icon v-slot:default v-if="isDeliverable(order)" color="success" size="16">fa-check-circle</v-icon>
                <input v-else type="checkbox" :checked="isOrderSelected(order.ID)" @change="toggleOrder(order)">
              </div>
              
              <div class="col-code">
                <a target="_blank" :href="(order.OrderType == 1 ? '/don-hang/' : '/ky-gui/') + order.ID" class="order-link font-weight-bold">
                  {{ (order.OrderType == 1 ? 'MD' : 'KG') + order.ID }} <v-icon size="10" color="primary">fa-external-link-alt</v-icon>
                </a>
                <div class="mt-1">
                  <span class="badge-status">{{ isDeliverable(order) ? 'Chờ giao' : 'Về kho VN' }}</span>
                </div>
              </div>

              <div class="col-goods">
                <div v-for="dec in order.Declarations" :key="dec.ID" class="goods-item mb-2">
                  <div class="goods-name font-weight-bold">{{ dec.MoTa }}</div>
                  <div class="goods-meta mt-1">
                    {{ dec.DonViTinh }} · {{ dec.GiaKhaiHQToiThieu | currency }} x {{ dec.SoLuong }} · VAT {{ dec.ThueVAT }}%
                    <span class="badge-policy ml-1" :style="getPolicyStyle(dec.Status)">
                      {{ dec.Status | display_value(statusOptions) }}
                    </span>
                  </div>
                </div>
                <div v-if="!order.Declarations || order.Declarations.length === 0" class="value-item font-italic" style="font-size: 12px;">
                  Không có kê khai hàng hóa
                </div>
              </div>

              <div class="col-value">
                <div class="value-item">Tổng HĐ: <b>{{ order.TotalInvoicePrice | currency }}</b></div>
                <div v-if="order.OrderType == 1 && order.MainOrderType === 'NKCN'" class="tax-info mt-1">
                  <div class="value-item">Phí UTNK: <b>{{ order.FeeUTNK | currency }}</b></div>
                  <div class="value-item">Thuế NK: <b>{{ order.TaxNKAmount | currency }}</b> ({{ order.TaxNKRate }}%)</div>
                  <div class="value-item">VAT: <b>{{ order.TaxVATAmount | currency }}</b> ({{ order.TaxVATRate }}%)</div>
                </div>
              </div>

              <div class="col-packages">
                <div v-for="pkg in order.Packages" :key="pkg.ID" class="package-item mb-1">
                  <span class="font-weight-bold text-dark">{{ pkg.TransactionCode }}</span>
                  <span class="value-item"> · {{ pkg.NumberOfPack || 1 }} kiện - {{ pkg.Weight }}kg - {{ pkg.Volume }}CBM - <v-icon size="11" color="green">fa-truck</v-icon> {{ pkg.VehicleCode || 'D012' }}</span>
                </div>
                <div class="package-summary mt-2">
                  Σ {{ getOrderPackagesTotal(order) }} kiện - {{ getOrderWeightTotal(order) }}kg - {{ getOrderVolumeTotal(order) }}CBM
                </div>
              </div>

              <div class="col-note">
                <v-textarea 
                  placeholder="Ghi chú..." 
                  dense 
                  outlined 
                  hide-details 
                  rows="2" 
                  class="note-textarea"
                  v-model="order.Note"
                ></v-textarea>
              </div>

              <div class="col-action">
                <v-btn 
                  v-if="!isDeliverable(order)" 
                  :color="cust.Wallet >= cust.PayLeft ? 'primary' : 'error'" 
                  class="text-none font-weight-bold" 
                  small
                  depressed
                  @click="openSingleConfirm(cust, order)"
                >
                  Xác nhận
                </v-btn>
                <span v-else class="status-confirmed">
                  <v-icon size="12" color="success" class="mr-1">fa-check</v-icon> Đã xác nhận
                </span>
              </div>
            </div>

            <!-- Order Finance Row -->
            <div class="order-finance-bar">
              <span class="order-finance-item">Tiền hàng: <b>{{ order.TotalAmountVND | currency }}</b></span>
              <span class="order-finance-item">Đã cọc: <b class="success--text">{{ order.Deposit | currency }}</b></span>
              <span class="order-finance-item">Còn thiếu: <b class="error--text">{{ (order.PayLeft !== undefined ? order.PayLeft : Math.max(0, (order.TotalAmountVND || 0) - (order.Deposit || 0))) | currency }}</b></span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Footer Note -->
      <div class="table-footer-note pa-3">
        Thanh toán <b>gộp theo khách</b>: Tổng giá trị đơn (lấy trên đơn gốc) - Tổng đã cọc = <b>Thiếu tổng</b>; Số dư ví - Thiếu tổng = <b>Dư sau xuất</b> (≥0 "Đã đủ", &lt;0 "Thiếu tiền" & bôi đỏ). Cột Hàng hóa hiển thị các kê khai của đơn (độc lập mã VĐ).
      </div>
    </v-card>

    <!-- Confirm Dialog -->
    <v-dialog v-model="dialogConfirm" max-width="500px" persistent>
      <v-card style="border-radius: 12px; overflow: hidden;">
        <v-card-title :class="confirmDialogColor" class="py-3">
          <span class="title font-weight-bold" style="font-size: 16px;">
            {{ confirmDialogTitle }}
          </span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialogConfirm = false"><v-icon>fa-times</v-icon></v-btn>
        </v-card-title>
        
        <v-card-text class="pa-4">
          <div class="font-weight-bold mb-1" style="font-size: 14px; color: #333;">{{ confirmData.customer.CustomerName }} (KH {{ confirmData.customer.UserCode }})</div>
          <div style="font-size: 12px; color: #666;" class="mb-3">Thanh toán tính <b>gộp theo khách</b>.</div>
          
          <div class="rounded mb-3" style="border: 1px solid #E5E7EB;">
            <div class="d-flex justify-space-between px-3 py-2" style="font-size: 13px;">
              <span style="color: #666;">Tổng giá trị đơn</span>
              <b style="color: #333;">{{ confirmData.summary.totalAmount | currency }}</b>
            </div>
            <div class="d-flex justify-space-between px-3 py-2" style="border-top: 1px solid #E5E7EB; font-size: 13px;">
              <span style="color: #666;">Tổng đã cọc</span>
              <b class="success--text">{{ confirmData.summary.deposit | currency }}</b>
            </div>
            <div class="d-flex justify-space-between px-3 py-2" style="border-top: 1px solid #E5E7EB; font-size: 13px;">
              <span style="color: #666;">Còn thiếu</span>
              <b :class="confirmData.summary.payLeft > 0 ? 'error--text' : 'success--text'">{{ confirmData.summary.payLeft | currency }}</b>
            </div>
            <div class="d-flex justify-space-between px-3 py-2" style="border-top: 1px solid #E5E7EB; font-size: 13px;">
              <span style="color: #666;">Số dư ví</span>
              <b class="primary--text">{{ confirmData.customer.Wallet | currency }}</b>
            </div>
            <div class="d-flex justify-space-between px-3 py-2" style="border-top: 1px solid #E5E7EB; font-size: 13px;">
              <span style="color: #666;">Dư ví sau thanh toán</span>
              <b :class="confirmData.isShortfall ? 'error--text' : 'success--text'">{{ confirmData.summary.walletAfterPayment | currency }}</b>
            </div>
          </div>

          <!-- Additional inputs for Ship Fee / Other Fee -->
          <v-expansion-panels flat v-if="confirmData.actionType !== 'customer_payment' && !confirmData.isShortfall">
            <v-expansion-panel>
              <v-expansion-panel-header class="pa-0" style="min-height: 36px; font-size: 13px; color: #666;">
                Tuỳ chọn phí nâng cao
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pa-0">
                <v-layout row wrap>
                  <v-flex xs6 class="pr-1">
                    <h-currency-input label="Phí ship" v-model="confirmData.shipFee" :decimal="0" dense outlined></h-currency-input>
                  </v-flex>
                  <v-flex xs6 class="pl-1">
                    <h-currency-input label="Phí khác" v-model="confirmData.feeOther" :decimal="0" dense outlined></h-currency-input>
                  </v-flex>
                </v-layout>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <div
            v-if="showDeductWalletOption"
            class="mt-3 pa-3 rounded"
            style="border: 1px solid #D1D5DB; background: #F9FAFB;"
          >
            <v-checkbox
              v-model="confirmData.deductWallet"
              hide-details
              class="mt-0 pt-0"
              label="Trừ tiền ví khách hàng"
            ></v-checkbox>
            <div style="font-size: 12px; color: #6B7280; margin-top: -4px;">
              Bỏ tích nếu chỉ xác nhận cho giao, không trừ ví và không thanh toán đơn hàng.
            </div>
          </div>

          <div class="pa-3 rounded d-flex mt-2" :style="{ background: confirmData.isShortfall ? '#FEE2E2' : '#DCFCE7', color: confirmData.isShortfall ? '#991B1B' : '#15803D', fontSize: '13px' }">
            <v-icon size="16" :color="confirmData.isShortfall ? 'error' : 'success'" class="mr-2 mt-1">fa-exclamation-triangle</v-icon>
            <div>
              <template v-if="confirmData.actionType === 'customer_payment' && confirmData.isShortfall">
                Khách hàng còn thiếu <b>{{ confirmData.summary.payLeft | currency }}</b>. Chỉ khi <b>còn thiếu = 0</b> mới được thanh toán cả khách.
              </template>
              <template v-else-if="confirmData.actionType === 'customer_payment'">
                Hệ thống sẽ thanh toán toàn bộ <b>{{ confirmData.orders.length }} đơn đã xác nhận và chưa thanh toán</b> của khách này.
              </template>
              <template v-else-if="confirmData.isShortfall">
                Khách <b>thiếu tiền</b> (dư ví âm). Xác nhận sẽ chỉ chuyển <b>{{ confirmData.orders.length }} đơn</b> sang Chờ giao (không trừ tiền ví).
              </template>
              <template v-else-if="!confirmData.deductWallet">
                Đủ tiền. Xác nhận sẽ chỉ chuyển <b>{{ confirmData.orders.length }} đơn</b> sang Chờ giao, <b>không trừ tiền ví</b> và <b>không thanh toán</b>.
              </template>
              <template v-else>
                Đủ tiền. Xác nhận sẽ thanh toán và chuyển <b>{{ confirmData.orders.length }} đơn</b> sang Chờ giao.
              </template>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn text @click="dialogConfirm = false">Hủy</v-btn>
          <v-btn 
            :color="confirmActionColor"
            class="white--text text-none px-4" 
            :loading="confirming"
            :disabled="isConfirmActionDisabled"
            @click="executeConfirm"
          >
            <v-icon size="14" class="mr-2">{{ confirmActionIcon }}</v-icon>
            {{ confirmActionLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog xác nhận đơn NKCN chưa có thuế VAT -->
    <v-dialog v-model="vatConfirmDialog.show" persistent max-width="480">
      <v-card class="elevation-4" style="border-radius: 10px; overflow: hidden;">
        <v-card-title class="pa-4 d-flex align-center orange darken-2 white--text">
          <v-avatar color="white" size="36" class="mr-3">
            <v-icon color="orange darken-2" size="20">fa-exclamation-triangle</v-icon>
          </v-avatar>
          <div>
            <div class="subtitle-1 font-weight-bold">Xác nhận thanh toán đơn NKCN</div>
            <div class="caption white--text text--lighten-4">Chưa có thông tin thuế VAT</div>
          </div>
        </v-card-title>
        <v-card-text class="pa-5 pt-4">
          <v-alert dense outlined type="warning" class="mb-3">
            {{ vatConfirmDialog.message }}
          </v-alert>
          <div style="font-size: 13px; color: #4B5563; line-height: 1.5;">
            Đơn hàng loại NKCN chưa có thuế VAT (TaxVATAmount > 0). Bạn có chắc chắn muốn bỏ qua kiểm tra thuế VAT và tiếp tục thanh toán không?
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="vatConfirmDialog.show = false">Hủy</v-btn>
          <v-btn
            color="orange darken-2"
            class="white--text px-4"
            @click="onVatConfirmOk"
          >
            <v-icon size="14" class="mr-2">fa-check</v-icon>
            Đồng ý thanh toán
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import configs from "../../configs";
import _ from "lodash";
import { currency } from "@/commons/currency";
import { sendErrorNotice } from "@/commons/utils";

export default {
  data() {
    return {
      quickSearchTerm: "",
      selectedCustomerFilter: 'all',
      selectedPaymentFilter: 'all',
      selectedDeliverableFilter: 'undeliverable',
      pagination: {
        page: 1,
        rowsPerPage: 20
      },
      customerDetails: {},
      loadingDetails: {},
      selectedOrders: [],
      loading: false,
      dialogConfirm: false,
      confirming: false,
      vatConfirmDialog: {
        show: false,
        message: ''
      },
      confirmData: {
        customer: {},
        orders: [],
        actionType: "confirm_delivery",
        isShortfall: false,
        deductWallet: true,
        shipFee: 0,
        feeOther: 0,
        feeOtherName: "",
        summary: {
          totalAmount: 0,
          deposit: 0,
          payLeft: 0,
          walletAfterPayment: 0
        }
      },
      statusOptions: configs.DECLARE_STATUS_LIST
    };
  },
  watch: {
    payOrders: {
      handler: function(newPayOrders) {
        if (newPayOrders && newPayOrders.length > 0) {
          this.loadCustomerDetails(newPayOrders);
        } else {
          this.loading = false;
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapState("payOrder", {
      payOrders: "all",
      totalPayOrders: "total"
    }),
    canViewCustomerAddress() {
      const roleType = Number(this.$store.state.authentication.user && this.$store.state.authentication.user.roletype) || 0;
      const isAdmin = (roleType & configs.ROLE_TYPE.Admin) === configs.ROLE_TYPE.Admin;
      const isPayment = (roleType & configs.ROLE_TYPE.Payment) === configs.ROLE_TYPE.Payment;
      return isAdmin || isPayment;
    },
    isAllSelected() {
      let undeliveredCount = 0;
      this.filteredPayOrders.forEach(cust => {
        undeliveredCount += this.getCustomerOrders(cust.ID).filter(o => !this.isDeliverable(o)).length;
      });
      if (undeliveredCount === 0) return false;
      return this.selectedOrders.length === undeliveredCount;
    },
    customerList() {
      let list = [{ ID: 'all', CustomerName: 'Tất cả khách hàng' }];
      let masterCusts = (this.$store.state.customer && this.$store.state.customer.master && this.$store.state.customer.master.data) || [];
      if (masterCusts.length > 0) {
        masterCusts.forEach(c => {
          list.push({
            ID: c.ID,
            CustomerName: `${c.UserName || c.FullName || ''} (${c.UserCode || ''})`
          });
        });
      } else if (this.payOrders && this.payOrders.length > 0) {
        this.payOrders.forEach(c => {
          list.push({
            ID: c.ID,
            CustomerName: `${c.CustomerName || ''} (${c.UserCode || ''})`
          });
        });
      }
      return list;
    },
    totalPages() {
      const total = Number(this.totalPayOrders) || 0;
      const rpp = Number(this.pagination.rowsPerPage) || 20;
      return Math.max(1, Math.ceil(total / rpp));
    },
    paymentFilterList() {
      return [
        { value: 'all', text: 'Mọi thanh toán' },
        { value: 'sufficient', text: 'Đủ tiền' },
        { value: 'shortfall', text: 'Thiếu tiền' }
      ];
    },
    deliverableFilterList() {
      return [
        { value: 'all', text: 'Mọi tình trạng duyệt XK' },
        { value: 'deliverable', text: 'Đã duyệt xuất kho' },
        { value: 'undeliverable', text: 'Chưa duyệt xuất kho' }
      ];
    },
    filteredPayOrders() {
      if (!this.payOrders) return [];
      let result = this.payOrders;
      
      if (this.selectedDeliverableFilter !== 'all') {
        result = result.filter(c => {
          if (!this.customerDetails[c.ID]) return true;
          return this.getCustomerOrders(c.ID).length > 0;
        });
      }
      
      return result;
    },
    totalCustomers() {
      return this.filteredPayOrders.length;
    },
    totalOrders() {
      return this.filteredPayOrders.reduce((sum, c) => sum + this.getVisibleOrderCount(c.ID), 0);
    },
    unconfirmedCount() {
      let count = 0;
      this.filteredPayOrders.forEach(cust => {
        let orders = this.getCustomerOrders(cust.ID);
        if (orders.length > 0) {
          count += orders.filter(o => !this.isDeliverable(o)).length;
        } else if (this.selectedDeliverableFilter === 'all') {
          count += cust.OrderNumber;
        }
      });
      return count;
    },
    confirmedCount() {
      let count = 0;
      this.filteredPayOrders.forEach(cust => {
        count += this.getCustomerOrders(cust.ID).filter(o => this.isDeliverable(o)).length;
      });
      return count;
    },
    totalShortfall() {
      return this.filteredPayOrders.reduce((sum, item) => {
        let diff = item.Wallet - item.PayLeft;
        return diff < 0 ? sum + Math.abs(diff) : sum;
      }, 0);
    },
    confirmDialogTitle() {
      if (this.confirmData.actionType === "customer_payment") {
        return `Thanh toán cả khách ${this.confirmData.orders.length} đơn`;
      }
      if (this.confirmData.orders.length > 1) {
        return `Kế toán duyệt ${this.confirmData.orders.length} đơn`;
      }
      if (this.confirmData.orders.length === 1) {
        return `Kế toán duyệt đơn ${(this.confirmData.orders[0].OrderType == 1 ? 'MD' : 'KG') + this.confirmData.orders[0].ID}`;
      }
      return "Kế toán duyệt đơn";
    },
    confirmDialogColor() {
      if (this.confirmData.actionType === "customer_payment") {
        return this.confirmData.isShortfall ? "error white--text" : "success white--text";
      }
      return this.confirmData.isShortfall ? "error white--text" : "primary white--text";
    },
    confirmActionLabel() {
      if (this.confirmData.actionType === "customer_payment") {
        return "Thanh toán";
      }
      return this.confirmData.isShortfall ? "Vẫn xác nhận cho giao" : "Xác nhận cho giao";
    },
    confirmActionIcon() {
      return this.confirmData.actionType === "customer_payment" ? "fa-wallet" : "fa-clipboard-check";
    },
    confirmActionColor() {
      if (this.confirmData.actionType === "customer_payment") {
        return "success";
      }
      return this.confirmData.isShortfall ? "error" : "success";
    },
    showDeductWalletOption() {
      return this.confirmData.actionType === "confirm_delivery" && !this.confirmData.isShortfall;
    },
    isConfirmActionDisabled() {
      return this.confirmData.actionType === "customer_payment" && this.confirmData.isShortfall;
    },
    selectedStats() {
      let packages = 0;
      let shortfall = 0;
      let rows = this.selectedOrders.length;
      
      let selectedCustomers = new Set();
      this.selectedOrders.forEach(o => {
        packages += this.getOrderPackagesTotal(o);
        selectedCustomers.add(o._customerID);
      });
      
      selectedCustomers.forEach(cid => {
        let cust = this.payOrders.find(c => c.ID === cid);
        if (cust) {
          let diff = cust.Wallet - cust.PayLeft;
          if (diff < 0) shortfall += Math.abs(diff);
        }
      });
      
      return { packages, rows, shortfall };
    }
  },
  methods: {
    filter_data() {
      this.loading = true;
      let filter = [];
      if (this.selectedCustomerFilter && this.selectedCustomerFilter !== 'all') {
        filter.push({
          column: 'ID',
          value: this.selectedCustomerFilter,
          condition: 'equal'
        });
      }
      if (this.selectedPaymentFilter === 'sufficient') {
        filter.push({
          column: 'Remain',
          value: 0,
          condition: 'equal'
        });
      } else if (this.selectedPaymentFilter === 'shortfall') {
        filter.push({
          column: 'Remain',
          value: 0,
          condition: 'greater_than'
        });
      }

      let quickFilter = [];
      if (this.quickSearchTerm && this.quickSearchTerm.trim()) {
        const term = this.quickSearchTerm.trim();
        quickFilter = [
          { column: 'CustomerName', value: term, condition: 'contain' },
          { column: 'UserCode', value: term, condition: 'contain' },
          { column: 'PhoneNumber', value: term, condition: 'contain' }
        ];
      }

      var param = {
        pagination: {
          page: this.pagination.page,
          itemsPerPage: this.pagination.rowsPerPage,
          sortBy: ["ID"],
          descending: [true],
          offset: (this.pagination.page - 1) * this.pagination.rowsPerPage
        },
        filter: filter,
        quickFilter: quickFilter
      };
      
      this.$store.dispatch("payOrder/getPayOrderList", param);
      this.customerDetails = {};
      this.selectedOrders = [];
    },
    onSearchInput: _.debounce(function() {
      this.pagination.page = 1;
      this.filter_data();
    }, 400),
    onClearSearch() {
      this.$nextTick(() => {
        this.quickSearchTerm = "";
        this.pagination.page = 1;
        this.filter_data();
      });
    },
    onCustomerFilterChange() {
      this.pagination.page = 1;
      this.filter_data();
    },
    onPaymentFilterChange() {
      this.pagination.page = 1;
      this.filter_data();
    },
    onDeliverableFilterChange() {
      this.pagination.page = 1;
      this.filter_data();
    },
    onRowsPerPageChange() {
      this.pagination.page = 1;
      this.filter_data();
    },
    async loadCustomerDetails(customers) {
      this.loading = true;
      const promises = customers.map(cust => {
        const req = { headers: { 'Authorization': `Bearer ${this.$store.state.authentication.token}` } };
        return this.$http.get(`${configs.API}/admin/api/pay_order/${cust.ID}`, req)
          .then(res => {
            if (res.data && res.data.data) {
              let custData = res.data.data;
              if (custData.Orders) {
                custData.Orders.forEach(o => o._customerID = cust.ID);
              }
              this.$set(this.customerDetails, cust.ID, custData);
            }
          })
          .catch(err => {
            // Error loading customer details
          });
      });
      
      await Promise.all(promises);
      this.loading = false;
    },
    prevPage() {
      if (this.pagination.page > 1) {
        this.pagination.page--;
        this.filter_data();
      }
    },
    nextPage() {
      if (this.pagination.page < this.totalPages) {
        this.pagination.page++;
        this.filter_data();
      }
    },
    isDeliverable(order) {
      if (!order) return false;
      return order.Deliverable === 1 || order.Deliverable === true || order.Deliverable === '1';
    },
    toggleSelectAll(e) {
      const checked = e.target.checked;
      if (checked) {
        let allOrders = [];
        this.filteredPayOrders.forEach(cust => {
          this.getCustomerOrders(cust.ID).forEach(o => {
            if (!this.isDeliverable(o)) {
              allOrders.push(o);
            }
          });
        });
        this.selectedOrders = allOrders;
      } else {
        this.selectedOrders = [];
      }
    },
    toggleSelectCustomer(cust) {
      let customerOrders = this.getCustomerOrders(cust.ID).filter(o => !this.isDeliverable(o));
      if (customerOrders.length === 0) return;
      let selectedFromCust = this.selectedOrders.filter(o => o._customerID === cust.ID);
      
      if (selectedFromCust.length === customerOrders.length) {
        this.selectedOrders = this.selectedOrders.filter(o => o._customerID !== cust.ID);
      } else {
        let otherCustOrders = this.selectedOrders.filter(o => o._customerID !== cust.ID);
        this.selectedOrders = [...otherCustOrders, ...customerOrders];
      }
    },
    isCustomerAllSelected(custID) {
      let undelivered = this.getCustomerOrders(custID).filter(o => !this.isDeliverable(o));
      if (undelivered.length === 0) return false;
      let selectedFromCust = this.selectedOrders.filter(o => o._customerID === custID);
      return selectedFromCust.length === undelivered.length;
    },
    getCustomerStaffName(cust, field) {
      if (cust && cust[field]) return cust[field];
      const detail = this.customerDetails[cust.ID];
      if (detail && detail[field]) return detail[field];
      return '---';
    },
    getCustomerPhone(cust) {
      if (cust && cust.PhoneNumber) return cust.PhoneNumber;
      const detail = this.customerDetails[cust.ID];
      if (detail && detail.PhoneNumber) return detail.PhoneNumber;
      return '';
    },
    getCustomerAddress(cust) {
      if (cust && cust.Address) return cust.Address;
      const detail = this.customerDetails[cust.ID];
      if (detail && detail.Address) return detail.Address;
      return '';
    },
    getCustomerTotalPackages(custID) {
      return this.getCustomerOrders(custID).reduce((sum, o) => sum + this.getOrderPackagesTotal(o), 0);
    },
    getCustomerOrders(custID) {
      let details = this.customerDetails[custID];
      if (!details || !details.Orders) return [];
      return this.filterOrdersByDeliverable(details.Orders);
    },
    filterOrdersByDeliverable(orders = []) {
      if (this.selectedDeliverableFilter === 'deliverable') {
        return orders.filter(o => this.isDeliverable(o));
      }
      if (this.selectedDeliverableFilter === 'undeliverable') {
        return orders.filter(o => !this.isDeliverable(o));
      }
      return orders;
    },
    getVisibleOrderCount(custID) {
      return this.getCustomerOrders(custID).length;
    },
    isOrderSelected(id) {
      return this.selectedOrders.some(o => o.ID === id);
    },
    toggleOrder(order) {
      if (this.isOrderSelected(order.ID)) {
        this.selectedOrders = this.selectedOrders.filter(o => o.ID !== order.ID);
      } else {
        this.selectedOrders.push(order);
      }
    },
    clearSelection() {
      this.selectedOrders = [];
    },
    getOrderPackagesTotal(order) {
      if (!order.Packages || order.Packages.length === 0) return order.TotalPackage || 0;
      return order.Packages.reduce((sum, p) => sum + (parseInt(p.NumberOfPack) || 1), 0);
    },
    getOrderWeightTotal(order) {
      if (!order.Packages) return 0;
      return order.Packages.reduce((sum, p) => sum + (p.Weight || 0), 0).toFixed(1);
    },
    getOrderVolumeTotal(order) {
      if (!order.Packages) return 0;
      return order.Packages.reduce((sum, p) => sum + (p.Volume || 0), 0).toFixed(2);
    },
    getUTNKRate(order) {
      const base = (order.AmountVND || 0) + (order.FeeShipCNVND || 0);
      if (base <= 0) return '0.0%';
      return ((order.FeeUTNK || 0) / base * 100).toFixed(1) + '%';
    },
    getUndeliveredCount(custID) {
      let orders = this.getCustomerOrders(custID);
      if (orders.length === 0) {
        let cust = this.payOrders.find(c => c.ID === custID);
        if (!cust || this.selectedDeliverableFilter !== 'all') return 0;
        return cust.OrderNumber;
      }
      return orders.filter(o => !this.isDeliverable(o)).length;
    },
    hasUndeliveredOrders(custID) {
      return this.getUndeliveredCount(custID) > 0;
    },
    getConfirmedUnpaidOrders(custID) {
      return this.getCustomerOrders(custID).filter(o => this.isDeliverable(o));
    },
    getConfirmedUnpaidCount(custID) {
      return this.getConfirmedUnpaidOrders(custID).length;
    },
    hasConfirmedUnpaidOrders(custID) {
      return this.getConfirmedUnpaidCount(custID) > 0;
    },
    buildConfirmSummary(customer, orders) {
      const totalAmount = orders.reduce((sum, order) => sum + (Number(order.TotalAmountVND) || 0), 0);
      const deposit = orders.reduce((sum, order) => sum + (Number(order.Deposit) || 0), 0);
      const payLeft = Math.max(0, Math.round(totalAmount - deposit));
      const wallet = Number(customer.Wallet) || 0;
      return {
        totalAmount,
        deposit,
        payLeft,
        walletAfterPayment: wallet - payLeft
      };
    },
    getPolicyStyle(policy) {
      if (!policy) return { background: '#EFF6FF', color: '#1D4ED8' };
      // find in statusOptions
      const option = this.statusOptions.find(opt => opt.value === policy);
      return {
        background: option.color,
        color: option.textColor
      };
    },
    shouldOnlyDeliverable() {
      if (this.confirmData.actionType === "customer_payment") {
        return false;
      }
      if (this.confirmData.isShortfall) {
        return true;
      }
      return !this.confirmData.deductWallet;
    },
    confirmCustomer(cust) {
      let undelivered = this.getCustomerOrders(cust.ID).filter(o => !this.isDeliverable(o));
      if (undelivered.length === 0) {
        sendErrorNotice("Không có đơn hàng nào cần xác nhận.");
        return;
      }
      this.confirmData = {
        customer: cust,
        orders: undelivered,
        actionType: "confirm_delivery",
        isShortfall: (cust.Wallet - cust.PayLeft) < 0,
        deductWallet: true,
        shipFee: 0,
        feeOther: 0,
        feeOtherName: "",
        summary: this.buildConfirmSummary(cust, undelivered)
      };
      this.dialogConfirm = true;
    },
    openSingleConfirm(customer, order) {
      this.confirmData = {
        customer: customer,
        orders: [order],
        actionType: "confirm_delivery",
        isShortfall: (customer.Wallet - customer.PayLeft) < 0,
        deductWallet: true,
        shipFee: 0,
        feeOther: 0,
        feeOtherName: "",
        summary: this.buildConfirmSummary(customer, [order])
      };
      this.dialogConfirm = true;
    },
    openBulkConfirm() {
      if (this.selectedOrders.length === 0) return;
      
      let customerIds = [...new Set(this.selectedOrders.map(o => o._customerID))];
      if (customerIds.length > 1) {
        sendErrorNotice("Vui lòng chỉ chọn các đơn hàng của CÙNG MỘT khách hàng để xác nhận hàng loạt.");
        return;
      }
      
      let customerId = customerIds[0];
      let customer = this.payOrders.find(c => c.ID === customerId);
      
      this.confirmData = {
        customer: customer,
        orders: [...this.selectedOrders],
        actionType: "confirm_delivery",
        isShortfall: (customer.Wallet - customer.PayLeft) < 0,
        deductWallet: true,
        shipFee: 0,
        feeOther: 0,
        feeOtherName: "",
        summary: this.buildConfirmSummary(customer, this.selectedOrders)
      };
      this.dialogConfirm = true;
    },
    openCustomerPaymentConfirm(customer) {
      const confirmedOrders = this.getConfirmedUnpaidOrders(customer.ID);
      if (confirmedOrders.length === 0) {
        sendErrorNotice("Không có đơn đã xác nhận và chưa thanh toán.");
        return;
      }
      const summary = this.buildConfirmSummary(customer, confirmedOrders);
      this.confirmData = {
        customer,
        orders: confirmedOrders,
        actionType: "customer_payment",
        isShortfall: summary.walletAfterPayment < 0,
        deductWallet: true,
        shipFee: 0,
        feeOther: 0,
        feeOtherName: "",
        summary
      };
      this.dialogConfirm = true;
    },
    executeConfirm(ignoreVatCheck = false) {
      if (typeof ignoreVatCheck !== 'boolean') {
        ignoreVatCheck = false;
      }
      if (this.isConfirmActionDisabled) return;

      this.confirming = true;
      
      let order_ids = _.map(_.filter(this.confirmData.orders, {'OrderType': 1}), "ID");
      let deposit_ids = _.map(_.filter(this.confirmData.orders, {'OrderType': 2}), "ID");
      const onlyDeliverable = this.shouldOnlyDeliverable();
      
      if (!onlyDeliverable && !ignoreVatCheck) {
        let invalidOrders = _.filter(this.confirmData.orders, o => {
          return o.OrderType == 1 && o.MainOrderType === 'NKCN' && (!o.TaxVATAmount || o.TaxVATAmount <= 0);
        });
        if (invalidOrders.length > 0) {
          let invalidIDs = _.map(invalidOrders, "ID").join(", ");
          this.vatConfirmDialog = {
            show: true,
            message: `Đơn hàng ${invalidIDs} (loại NKCN) chưa có thông tin thuế VAT (TaxVATAmount > 0).`
          };
          this.confirming = false;
          return;
        }
      }
      
      let payload = {
        order_ids: order_ids,
        deposit_ids: deposit_ids,
        cash: 0,
        method: configs.RECHARGE_TYPE.Cash,
        shipFee: this.confirmData.actionType === "customer_payment" ? 0 : this.confirmData.shipFee,
        feeOther: this.confirmData.actionType === "customer_payment" ? 0 : this.confirmData.feeOther,
        feeOtherName: this.confirmData.feeOtherName,
        customer_id: this.confirmData.customer.ID,
        shippingMethod: "",
        Addresss: this.confirmData.customer.Address || "",
        PhoneNumber: this.confirmData.customer.PhoneNumber || "",
        only_deliverable: onlyDeliverable,
        ignore_vat_check: ignoreVatCheck
      };
      
      this.$store.dispatch("payOrder/updatePayOrder", payload);
      
      if (this.confirmData.actionType !== "customer_payment") {
        this.$store.dispatch("orderTransaction/updateMultiple", {
          OrderIDs: order_ids,
          DepositIDs: deposit_ids,
          ShippingInfo: `${this.confirmData.customer.CustomerName} - ${payload.PhoneNumber} - ${payload.Addresss}`,
          ShippingMethod: ""
        });
      }
      
      setTimeout(() => {
        this.confirming = false;
        this.dialogConfirm = false;
        this.selectedOrders = [];
        this.filter_data();
      }, 600);
    },
    onVatConfirmOk() {
      this.vatConfirmDialog.show = false;
      this.executeConfirm(true);
    }
  },
  mounted() {
    this.$store.dispatch('customer/getCustomerList', { is_master: 1, fields: ['ID', 'UserName', 'UserCode', 'FullName'] });
    this.filter_data();
    this.$root.$on("REQUEST_REFRESH_DATA", this.filter_data);
  },
  beforeDestroy() {
    this.$root.$off("REQUEST_REFRESH_DATA", this.filter_data);
  }
};
</script>

<style scoped>
/* Main Container Styling */
.accounting-container {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  color: #333;
}
.accounting-title {
  font-size: 20px;
  color: #111827;
  margin: 0 0 4px 0;
}
.accounting-subtitle {
  font-size: 13px;
  color: #4B5563;
}

/* KPI Cards Layout */
.kpi-card {
  background: #FFF;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  min-width: 100px;
  padding: 8px 16px;
  text-align: center;
}
.kpi-title {
  font-size: 11px;
  color: #6B7280;
  margin-bottom: 2px;
  font-weight: 500;
}
.kpi-value {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}
.card-shortfall {
  border-color: #FCA5A5;
  background: #FFF5F5;
  min-width: 140px;
}
.val-shortfall {
  color: #DC2626;
}

/* Selection / Action Bar */
.bulk-action-bar {
  background: #EAF2FE;
  border: 1px solid #3B82F6;
  border-radius: 6px;
  padding: 8px 16px;
}
.bulk-text {
  font-size: 13px;
  color: #1E3A8A;
}

/* Custom Table Layout */
.accounting-table-wrapper {
  width: 100%;
  overflow-x: auto;
}
.accounting-table-header {
  display: flex;
  background: #EEF3FB;
  border-bottom: 1px solid #D1D5DB;
  font-weight: 600;
  font-size: 12.5px;
  color: #374151;
  padding: 10px 12px;
  align-items: center;
}

/* Column Widths Definition */
.col-check {
  width: 40px;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.col-code {
  width: 120px;
  min-width: 120px;
  padding-right: 8px;
}
.col-goods {
  flex: 2;
  min-width: 250px;
  padding-right: 12px;
}
.col-value {
  width: 180px;
  min-width: 180px;
  padding-right: 12px;
}
.col-packages {
  flex: 2;
  min-width: 280px;
  padding-right: 12px;
}
.col-note {
  width: 160px;
  min-width: 160px;
  padding-right: 8px;
}
.col-action {
  width: 110px;
  min-width: 110px;
  text-align: center;
}

/* Customer Headers style */
.customer-group {
  border-bottom: 2px solid #E5E7EB;
}
.customer-row {
  display: flex;
  align-items: center;
  background: #F3F4F6;
  border-top: 1px solid #E5E7EB;
  border-bottom: 1px solid #E5E7EB;
  padding: 8px 12px;
}
.customer-info-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 2px;
  flex: 1;
  min-width: 0;
}
.customer-info-main {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.customer-address-line {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #4B5563;
  line-height: 1.3;
  margin-top: 1px;
}
.customer-address-text {
  font-weight: 500;
  color: #374151;
}
.customer-name {
  font-weight: 700;
  font-size: 13.5px;
  color: #1E3A8A;
}
.customer-meta {
  font-size: 12px;
  color: #4B5563;
}
.customer-action-wrapper {
  margin-left: auto;
}

/* Finance Row Under Customer Header */
.customer-finance-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  background: #F9FAFB;
  padding: 6px 12px 6px 52px;
  border-bottom: 1px solid #E5E7EB;
  gap: 16px;
  font-size: 12px;
}
.finance-label {
  font-weight: 600;
  color: #374151;
}
.finance-item {
  color: #4B5563;
}
.finance-status-badge {
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 700;
}
.badge-sufficient {
  background: #DCFCE7;
  color: #15803D;
}
.badge-shortfall {
  background: #FEE2E2;
  color: #991B1B;
}

/* Order Rows Under Customer */
.order-item-wrapper {
  border-bottom: 1px solid #E5E7EB;
  background: #FFF;
  transition: background-color 0.15s ease;
}
.order-item-wrapper:hover {
  background: #F9FAFB;
}
.order-item-wrapper.order-selected {
  background: #F0F7FF !important;
}
.order-row {
  display: flex;
  padding: 10px 12px;
  background: transparent;
  align-items: flex-start;
}
.order-finance-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 4px 12px 6px 52px;
  border-top: 1px dashed #EEF2F6;
  gap: 16px;
  font-size: 12px;
  background: #FAFAFA;
}
.order-item-wrapper.order-selected .order-finance-bar {
  background: transparent;
}
.order-finance-item {
  color: #4B5563;
}
.order-finance-item b {
  color: #1F2937;
}

/* Internal Styling inside columns */
.order-link {
  font-size: 13px;
  color: #1D4ED8;
  text-decoration: none;
}
.order-link:hover {
  text-decoration: underline;
}
.badge-status {
  background: #F3E8FF;
  color: #7E22CE;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

/* Goods/Declarations */
.goods-item {
  border-bottom: 1px dashed #E5E7EB;
  padding-bottom: 4px;
}
.goods-item:last-child {
  border-bottom: none;
}
.goods-name {
  font-size: 12.5px;
  color: #1F2937;
  line-height: 1.4;
}
.goods-meta {
  font-size: 11.5px;
  color: #6B7280;
}
.badge-policy {
  font-size: 10px;
  font-weight: bold;
  padding: 1px 4px;
  border-radius: 3px;
}

/* Value Column */
.value-item {
  font-size: 12px;
  color: #374151;
  margin-bottom: 2px;
}
.tax-info {
  font-size: 11.5px;
}

/* Package Column */
.package-item {
  font-size: 11.5px;
  line-height: 1.3;
}
.package-summary {
  font-size: 12px;
  font-weight: bold;
  color: #1D4ED8;
}

/* Note Textarea */
.note-textarea >>> .v-input__control >>> .v-input__slot {
  min-height: 36px !important;
  background: #FFF !important;
}
.note-textarea >>> textarea {
  font-size: 11.5px !important;
  padding: 4px 0 !important;
  line-height: 1.3 !important;
}

/* Action Column */
.status-confirmed {
  font-size: 12px;
  font-weight: 700;
  color: #15803D;
  background: #DCFCE7;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
}

/* Footer note */
.table-footer-note {
  background: #F9FAFB;
  font-size: 11.5px;
  color: #4B5563;
  line-height: 1.4;
  border-top: 1px solid #E5E7EB;
}

/* Checkbox Styling override */
input[type="checkbox"] {
  width: 15px;
  height: 15px;
  cursor: pointer;
  border-radius: 3px;
  border: 1px solid #D1D5DB;
}
</style>
